import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getAllProducts, createProduct, updateProduct, deleteProduct,
  getPromoCodes, createPromoCode, updatePromoCode, deletePromoCode,
  getSettings, updateSettings,
  getFlavors, createFlavor, updateFlavor, deleteFlavor,
  uploadImage
} from '../services/api';
import {
  Package, Tag, Plus, Pencil, Trash2, X, Check, ChevronLeft,
  ToggleLeft, ToggleRight, Search, Save, AlertCircle, LogOut,
  Settings as SettingsIcon, Image as ImageIcon, Droplets
} from 'lucide-react';

const AdminPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('products');

  useEffect(() => {
    if (localStorage.getItem('seltz_admin_auth') !== 'true') {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('seltz_admin_auth');
    localStorage.removeItem('seltz_admin_user');
    navigate('/admin/login');
  };

  return (
    <main className="min-h-screen bg-gray-50 pt-20" data-testid="admin-page">
      {/* Header */}
      <section className="bg-[#0a0a0a] py-10">
        <div className="max-w-6xl mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm font-bold mb-4 transition-colors"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <ChevronLeft size={14} /> înapoi la site
          </Link>
          <div className="flex items-center justify-between">
            <h1
              className="text-white font-black italic text-3xl"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              admin panel
            </h1>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/60 hover:text-white text-sm font-bold transition-colors"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              <LogOut size={16} /> logout
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 flex gap-2 py-3">
          {[
            { id: 'products', label: 'Pachete', icon: <Package size={16} /> },
            { id: 'flavors', label: 'Arome', icon: <Droplets size={16} /> },
            { id: 'promos', label: 'Coduri Reducere', icon: <Tag size={16} /> },
            { id: 'settings', label: 'Setări Site', icon: <SettingsIcon size={16} /> },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-black italic text-sm transition-all ${tab === t.id
                ? 'bg-[#0a0a0a] text-white shadow-md'
                : 'bg-gray-100 text-[#0a0a0a] hover:bg-gray-200'
                }`}
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {tab === 'products' && <ProductsAdmin />}
        {tab === 'flavors' && <FlavorsAdmin />}
        {tab === 'promos' && <PromosAdmin />}
        {tab === 'settings' && <SettingsAdmin />}
      </div>
    </main>
  );
};

// ============ PRODUCTS ADMIN ============
const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    getAllProducts()
      .then(setProducts)
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (product) => {
    await updateProduct(product.id, { active: !product.active });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să dezactivezi acest produs?')) return;
    await deleteProduct(id);
    load();
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  if (editing) {
    return <ProductForm product={editing === 'new' ? null : editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="admin-product-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută produse..."
            className="w-full pl-9 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          />
        </div>
        <button
          data-testid="admin-add-product"
          onClick={() => setEditing('new')}
          className="flex items-center gap-2 bg-[#0a0a0a] text-white font-black italic text-sm px-5 py-2.5 rounded-xl hover:bg-[#222222] transition-colors shadow-md"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          <Plus size={16} /> adaugă produs
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#0a0a0a] border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full" data-testid="admin-products-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Produs', 'Preț', 'Cutii', 'Status', 'Acțiuni'].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-[#0a0a0a] font-black italic text-xs uppercase tracking-wider" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#0a0a0a] rounded-lg flex items-center justify-center flex-shrink-0">
                          <img src={p.image} alt={p.name} className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                        <div>
                          <p className="text-[#0a0a0a] font-black italic text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.name}</p>
                          <p className="text-gray-400 text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[#0a0a0a] font-black text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>${p.price}</td>
                    <td className="px-5 py-4 text-gray-600 text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.cans}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => handleToggle(p)} className="flex items-center gap-1">
                        {p.active ? (
                          <span className="flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: 'Nunito, sans-serif' }}>
                            <ToggleRight size={14} /> Activ
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 bg-red-100 text-red-600 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: 'Nunito, sans-serif' }}>
                            <ToggleLeft size={14} /> Inactiv
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} className="p-2 text-[#0a0a0a] hover:bg-blue-50 rounded-lg transition-colors">
                          <Pencil size={14} />
                        </button>
                        <button data-testid={`delete-product-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden grid gap-3" data-testid="admin-products-mobile">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl shadow-sm p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-14 h-14 bg-[#0a0a0a] rounded-xl flex items-center justify-center flex-shrink-0">
                    <img src={p.image} alt={p.name} className="w-11 h-11 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0a0a0a] font-black italic text-sm truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.name}</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.id}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-[#0a0a0a] font-black text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>${p.price}</span>
                    <span className="text-gray-400 text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.cans} cutii</span>
                    <button onClick={() => handleToggle(p)}>
                      {p.active ? (
                        <span className="flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full"><ToggleRight size={12} /> Activ</span>
                      ) : (
                        <span className="flex items-center gap-1 bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full"><ToggleLeft size={12} /> Inactiv</span>
                      )}
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} className="p-2 text-[#0a0a0a] hover:bg-blue-50 rounded-lg"><Pencil size={14} /></button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400 bg-white rounded-2xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Package size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-bold">Niciun produs găsit</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============ PRODUCT FORM ============
const ProductForm = ({ product, onDone, onCancel }) => {
  const isNew = !product;
  const [form, setForm] = useState(
    product || {
      id: '', name: '', subtitle: '', description: '', longDescription: '',
      price: 0, cans: 8, flavors: [], image: '', badge: '', specs: '4.5% ABV • 100 Calories • 12 fl oz',
      tags: [], active: true,
    }
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState((product?.tags || []).join(', '));
  const [flavorsInput, setFlavorsInput] = useState((product?.flavors || []).join(', '));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : name === 'price' || name === 'cans' ? Number(value) : value }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await uploadImage(file);
      setForm(prev => ({ ...prev, image: res.url }));
    } catch (err) {
      alert('Eroare la încărcarea imaginii');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.id) return setError('ID și Numele sunt obligatorii');
    setSaving(true);
    const payload = {
      ...form,
      tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
      flavors: flavorsInput.split(',').map(f => f.trim()).filter(Boolean),
    };
    try {
      if (isNew) await createProduct(payload);
      else await updateProduct(form.id, payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-[#0a0a0a] font-black italic text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
          {isNew ? 'produs nou' : `editare: ${product.name}`}
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="ID (slug)" name="id" value={form.id} onChange={handleChange} disabled={!isNew} placeholder="ex: palm-springs-pack" />
          <FormField label="Nume" name="name" value={form.name} onChange={handleChange} placeholder="ex: palm springs pack" />
          <FormField label="Subtitlu" name="subtitle" value={form.subtitle} onChange={handleChange} />
          <FormField label="Preț ($)" name="price" value={form.price} onChange={handleChange} type="number" />
          <FormField label="Nr. Cutii" name="cans" value={form.cans} onChange={handleChange} type="number" />
          <FormField label="Badge" name="badge" value={form.badge || ''} onChange={handleChange} placeholder="ex: NEW" />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[#0a0a0a] font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Nunito, sans-serif' }}>Imagine Produs</label>
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 shadow-inner">
            <div className="w-24 h-24 bg-[#0a0a0a] rounded-xl flex items-center justify-center flex-shrink-0">
              <img
                src={form.image}
                className="w-20 h-20 object-contain"
                alt="Preview"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=?'; }}
              />
            </div>
            <div className="flex-1">
              <input
                name="image"
                value={form.image}
                onChange={handleChange}
                placeholder="URL Imagine (ex: /assets/images/product.png)"
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors mb-2"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              />
              <div className="flex items-center gap-2">
                <label className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-[10px] font-black italic px-3 py-1.5 rounded-lg cursor-pointer transition-colors">
                  ÎNCARCĂ POZĂ
                  <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                </label>
                <p className="text-gray-400 text-[10px] italic">sau introduceți calea</p>
              </div>
            </div>
          </div>
        </div>

        <FormField label="Specificații" name="specs" value={form.specs} onChange={handleChange} />
        <FormField label="Descriere scurtă" name="description" value={form.description} onChange={handleChange} textarea />
        <FormField label="Descriere lungă" name="longDescription" value={form.longDescription} onChange={handleChange} textarea />
        <FormField label="Tags (separate prin virgulă)" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="premium vodka, real fruit juice" />
        <FormField label="Arome IDs (separate prin virgulă)" value={flavorsInput} onChange={(e) => setFlavorsInput(e.target.value)} placeholder="pink-lemonade, mango" />
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" name="active" checked={form.active} onChange={handleChange} className="rounded" />
          <span className="text-sm font-bold text-[#0a0a0a]" style={{ fontFamily: 'Nunito, sans-serif' }}>Activ</span>
        </label>
        {error && <p className="text-red-500 text-sm font-bold flex items-center gap-1" style={{ fontFamily: 'Nunito, sans-serif' }}><AlertCircle size={14} /> {error}</p>}
        <div className="flex gap-3 mt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-product-btn"
            className="flex items-center gap-2 bg-[#0a0a0a] text-white font-black italic text-sm px-8 py-3 rounded-xl hover:bg-[#222222] transition-all shadow-md disabled:opacity-70"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <Save size={14} /> {saving ? 'se salvează...' : isNew ? 'creează produs' : 'salvează'}
          </button>
          <button type="button" onClick={onCancel} className="px-6 py-3 text-gray-500 font-bold text-sm rounded-xl hover:bg-gray-100 transition-colors" style={{ fontFamily: 'Nunito, sans-serif' }}>
            anulează
          </button>
        </div>
      </form>
    </div>
  );
};

// ============ PROMOS ADMIN ============
const PromosAdmin = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    getPromoCodes()
      .then(setPromos)
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (promo) => {
    await updatePromoCode(promo.id, { active: !promo.active });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi acest cod?')) return;
    await deletePromoCode(id);
    load();
  };

  if (editing) {
    return <PromoForm promo={editing === 'new' ? null : editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500 text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif' }}>
          {promos.length} cod{promos.length !== 1 ? 'uri' : ''} promoționale
        </p>
        <button
          data-testid="admin-add-promo"
          onClick={() => setEditing('new')}
          className="flex items-center gap-2 bg-[#0a0a0a] text-white font-black italic text-sm px-5 py-2.5 rounded-xl hover:bg-[#222222] transition-colors shadow-md"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          <Plus size={16} /> cod nou
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#0a0a0a] border-t-transparent" />
        </div>
      ) : (
        <div className="grid gap-4">
          {promos.map((p) => (
            <div key={p.id} data-testid={`promo-card-${p.code}`} className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 hover:shadow-md transition-shadow">
              {/* Code badge */}
              <div className="bg-[#0a0a0a] text-white font-black italic text-base sm:text-lg px-4 sm:px-5 py-2 sm:py-3 rounded-xl min-w-[100px] sm:min-w-[140px] text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {p.code}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className={`text-sm font-black italic ${p.type === 'percentage' ? 'text-green-600' : 'text-blue-600'}`} style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {p.type === 'percentage' ? `${p.value}% reducere` : `$${p.value} reducere`}
                  </span>
                  {p.minOrder > 0 && (
                    <span className="text-gray-400 text-xs font-semibold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      min. ${p.minOrder}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <span>Utilizări: {p.usedCount}{p.maxUses > 0 ? `/${p.maxUses}` : ' (nelimitat)'}</span>
                  {p.expiresAt && <span>Expiră: {new Date(p.expiresAt).toLocaleDateString('ro-RO')}</span>}
                </div>
              </div>
              {/* Status */}
              <button onClick={() => handleToggle(p)}>
                {p.active ? (
                  <span className="flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-3 py-1.5 rounded-full" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    <ToggleRight size={14} /> Activ
                  </span>
                ) : (
                  <span className="flex items-center gap-1 bg-red-100 text-red-600 text-xs font-bold px-3 py-1.5 rounded-full" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    <ToggleLeft size={14} /> Inactiv
                  </span>
                )}
              </button>
              {/* Actions */}
              <div className="flex gap-2">
                <button data-testid={`edit-promo-${p.code}`} onClick={() => setEditing(p)} className="p-2 text-[#0a0a0a] hover:bg-blue-50 rounded-lg transition-colors">
                  <Pencil size={14} />
                </button>
                <button data-testid={`delete-promo-${p.code}`} onClick={() => handleDelete(p.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {promos.length === 0 && (
            <div className="text-center py-12 text-gray-400 bg-white rounded-2xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Tag size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-bold">Niciun cod promoțional</p>
              <p className="text-sm mt-1">Creează primul cod de reducere</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ PROMO FORM ============
const PromoForm = ({ promo, onDone, onCancel }) => {
  const isNew = !promo;
  const [form, setForm] = useState(
    promo || { code: '', type: 'percentage', value: 10, minOrder: 0, maxUses: 0, active: true, expiresAt: '' }
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'value' || name === 'minOrder' || name === 'maxUses') ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.code.trim()) return setError('Codul este obligatoriu');
    if (form.value <= 0) return setError('Valoarea trebuie să fie pozitivă');
    setSaving(true);
    try {
      const payload = { ...form, expiresAt: form.expiresAt || null };
      if (isNew) await createPromoCode(payload);
      else await updatePromoCode(promo.id, payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-8 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-[#0a0a0a] font-black italic text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
          {isNew ? 'cod promoțional nou' : `editare: ${promo.code}`}
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Cod" name="code" value={form.code} onChange={handleChange} placeholder="ex: SELTZ10" />
          <div className="flex flex-col gap-1">
            <label className="text-[#0a0a0a] font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Nunito, sans-serif' }}>Tip</label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              className="border-2 border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors bg-white"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              <option value="percentage">Procentual (%)</option>
              <option value="fixed">Sumă Fixă ($)</option>
            </select>
          </div>
          <FormField label={form.type === 'percentage' ? 'Procent (%)' : 'Sumă ($)'} name="value" value={form.value} onChange={handleChange} type="number" />
          <FormField label="Comandă minimă ($)" name="minOrder" value={form.minOrder} onChange={handleChange} type="number" />
          <FormField label="Max utilizări (0 = nelimitat)" name="maxUses" value={form.maxUses} onChange={handleChange} type="number" />
          <FormField label="Data expirare" name="expiresAt" value={form.expiresAt ? form.expiresAt.split('T')[0] : ''} onChange={(e) => setForm(prev => ({ ...prev, expiresAt: e.target.value ? e.target.value + 'T00:00:00' : '' }))} type="date" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" name="active" checked={form.active} onChange={handleChange} className="rounded" />
          <span className="text-sm font-bold text-[#0a0a0a]" style={{ fontFamily: 'Nunito, sans-serif' }}>Activ</span>
        </label>
        {error && <p className="text-red-500 text-sm font-bold flex items-center gap-1" style={{ fontFamily: 'Nunito, sans-serif' }}><AlertCircle size={14} /> {error}</p>}
        <div className="flex gap-3 mt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-promo-btn"
            className="flex items-center gap-2 bg-[#0a0a0a] text-white font-black italic text-sm px-8 py-3 rounded-xl hover:bg-[#222222] transition-all shadow-md disabled:opacity-70"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <Save size={14} /> {saving ? 'se salvează...' : isNew ? 'creează cod' : 'salvează'}
          </button>
          <button type="button" onClick={onCancel} className="px-6 py-3 text-gray-500 font-bold text-sm rounded-xl hover:bg-gray-100 transition-colors" style={{ fontFamily: 'Nunito, sans-serif' }}>
            anulează
          </button>
        </div>
      </form>
    </div>
  );
};

// ============ FLAVORS ADMIN ============
const FlavorsAdmin = () => {
  const [flavors, setFlavors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    getFlavors()
      .then(setFlavors)
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi această aromă?')) return;
    await deleteFlavor(id);
    load();
  };

  const filtered = flavors.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.id.toLowerCase().includes(search.toLowerCase())
  );

  if (editing) {
    return <FlavorForm flavor={editing === 'new' ? null : editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută arome..."
            className="w-full pl-9 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          />
        </div>
        <button
          onClick={() => setEditing('new')}
          className="flex items-center gap-2 bg-[#0a0a0a] text-white font-black italic text-sm px-5 py-2.5 rounded-xl hover:bg-[#222222] transition-colors shadow-md"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          <Plus size={16} /> adaugă aromă
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#0a0a0a] border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl shadow-sm p-5 border border-gray-50 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-[#0a0a0a] rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src={f.image} alt={f.name} className="w-12 h-12 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div>
                  <h4 className="text-[#0a0a0a] font-black italic text-base" style={{ fontFamily: 'Nunito, sans-serif' }}>{f.name}</h4>
                  <p className="text-gray-400 text-xs font-semibold">{f.id}</p>
                </div>
              </div>
              <p className="text-gray-600 text-xs mb-4 line-clamp-2 min-h-[32px]">{f.description}</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full border border-gray-100" style={{ backgroundColor: f.color }} title="Brand color" />
                  <div className="w-6 h-6 rounded-full border border-gray-100" style={{ backgroundColor: f.bgColor }} title="Bg color" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(f)} className="p-2 text-[#0a0a0a] hover:bg-blue-50 rounded-lg transition-colors">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => handleDelete(f.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-400 bg-white rounded-2xl">
              <Droplets size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-bold">Nicio aromă găsită</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ FLAVOR FORM ============
const FlavorForm = ({ flavor, onDone, onCancel }) => {
  const isNew = !flavor;
  const [form, setForm] = useState(
    flavor || {
      id: '', name: '', description: '',
      color: '#000000', textColor: '#ffffff', bgColor: '#f0f0f0',
      image: '', availableIn: []
    }
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [availableInput, setAvailableInput] = useState((flavor?.availableIn || []).join(', '));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await uploadImage(file);
      setForm(prev => ({ ...prev, image: res.url }));
    } catch (err) {
      alert('Eroare la încărcarea imaginii');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.id) return setError('ID și Numele sunt obligatorii');
    setSaving(true);
    const payload = {
      ...form,
      availableIn: availableInput.split(',').map(i => i.trim()).filter(Boolean),
    };
    try {
      if (isNew) await createFlavor(payload);
      else await updateFlavor(form.id, payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-8 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-[#0a0a0a] font-black italic text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
          {isNew ? 'aromă nouă' : `editare aromă: ${flavor.name}`}
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="ID (slug)" name="id" value={form.id} onChange={handleChange} disabled={!isNew} placeholder="ex: sour-cherry" />
          <FormField label="Nume" name="name" value={form.name} onChange={handleChange} placeholder="ex: cherry" />
          <FormField label="Brand Color (HEX)" name="color" value={form.color} onChange={handleChange} placeholder="#d32f2f" />
          <FormField label="Text Color (HEX)" name="textColor" value={form.textColor} onChange={handleChange} placeholder="#ffffff" />
          <FormField label="Background Color (HEX)" name="bgColor" value={form.bgColor} onChange={handleChange} placeholder="#fce4e4" />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[#0a0a0a] font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Nunito, sans-serif' }}>Imagine Aromă</label>
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 shadow-inner">
            <div className="w-24 h-24 bg-[#0a0a0a] rounded-xl flex items-center justify-center flex-shrink-0">
              <img
                src={form.image}
                className="w-20 h-20 object-contain"
                alt="Preview"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=?'; }}
              />
            </div>
            <div className="flex-1">
              <input
                name="image"
                value={form.image}
                onChange={handleChange}
                placeholder="URL Imagine (ex: /assets/images/cherry.png)"
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors mb-2"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              />
              <div className="flex items-center gap-2">
                <label className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-[10px] font-black italic px-3 py-1.5 rounded-lg cursor-pointer transition-colors">
                  ÎNCARCĂ POZĂ
                  <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                </label>
                <p className="text-gray-400 text-[10px] italic">sau introduceți calea</p>
              </div>
            </div>
          </div>
        </div>

        <FormField label="Descriere" name="description" value={form.description} onChange={handleChange} textarea />
        <FormField label="Folosită în (IDs pachete)" value={availableInput} onChange={(e) => setAvailableInput(e.target.value)} placeholder="seltz-sour-cherry, seltz-pack" />

        {error && <p className="text-red-500 text-sm font-bold flex items-center gap-1"><AlertCircle size={14} /> {error}</p>}

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-[#0a0a0a] text-white font-black italic text-sm px-8 py-3 rounded-xl hover:bg-[#222222] transition-all shadow-md active:scale-95 disabled:opacity-70"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <Save size={14} /> {saving ? 'Se salvează...' : 'Salviază Aromă'}
          </button>
          <button type="button" onClick={onCancel} className="px-6 py-3 text-gray-500 font-bold text-sm rounded-xl hover:bg-gray-100 transition-colors">
            anulează
          </button>
        </div>
      </form>
    </div>
  );
};

// ============ SETTINGS ADMIN ============
const SettingsAdmin = () => {
  const [settings, setSettings] = useState({ heroImage: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getSettings()
      .then(data => setSettings(data || { heroImage: '/assets/images/hero_overlay.png' }))
      .catch(() => setSettings({ heroImage: '/assets/images/hero_overlay.png' }))
      .finally(() => setLoading(false));
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await uploadImage(file);
      setSettings(prev => ({ ...prev, heroImage: res.url }));
    } catch (err) {
      alert('Eroare la încărcarea imaginii');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    try {
      await updateSettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert('Eroare la salvarea setărilor');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-black border-t-transparent" /></div>;

  return (
    <div className="max-w-2xl bg-white rounded-2xl shadow-sm p-8">
      <h3 className="text-[#0a0a0a] font-black italic text-xl mb-6 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
        <ImageIcon size={20} /> Aspect Main Website
      </h3>

      <div className="space-y-6">
        <div>
          <label className="text-gray-500 font-bold text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Imagine Fundal Hero (Main)
          </label>
          <div className="flex flex-col gap-4">
            <div className="relative group">
              <img
                src={settings.heroImage}
                alt="Preview Hero"
                className="w-full h-48 object-cover rounded-xl border border-gray-200 shadow-inner bg-gray-50"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=Imagine+Invalida'; }}
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center text-white text-xs font-bold pointer-events-none">
                PREVIEW HERO
              </div>
            </div>
            <input
              type="text"
              value={settings.heroImage}
              onChange={(e) => setSettings({ ...settings, heroImage: e.target.value })}
              placeholder="Introduceți URL-ul imaginii (ex: /assets/images/hero.jpg)"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold text-sm focus:outline-none focus:border-black transition-colors mb-2"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            />
            <div className="flex items-center gap-3">
              <label className="bg-[#0a0a0a] hover:bg-[#222222] text-white text-xs font-black italic px-4 py-2 rounded-xl cursor-pointer transition-all shadow-md">
                ÎNCARCĂ IMAGINE NOUĂ
                <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
              </label>
              <p className="text-gray-400 text-xs italic">
                * Recomandat: 1920x1080px.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#0a0a0a] text-white font-black italic text-sm px-8 py-3 rounded-xl hover:bg-[#222222] transition-all shadow-md active:scale-95 disabled:opacity-70"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <Save size={16} /> {saving ? 'Se salvează...' : 'Salviază Modificări'}
          </button>

          {success && (
            <span className="flex items-center gap-1 text-green-600 font-bold text-sm animate-pulse">
              <Check size={16} /> Modificări salvate cu succes!
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ SHARED FORM FIELD ============
const FormField = ({ label, textarea, ...props }) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-[#0a0a0a] font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Nunito, sans-serif' }}>{label}</label>}
    {textarea ? (
      <textarea
        {...props}
        rows={3}
        className="border-2 border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors resize-none"
        style={{ fontFamily: 'Nunito, sans-serif' }}
      />
    ) : (
      <input
        {...props}
        className="border-2 border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors disabled:bg-gray-50 disabled:text-gray-400"
        style={{ fontFamily: 'Nunito, sans-serif' }}
      />
    )}
  </div>
);

export default AdminPage;
